import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardHeader,
  CircularProgress,
  Divider,
  TableHead,
  Table,
  TableBody,
  TableCell,
  TablePagination,
  TableRow,
  Select,
  MenuItem,
  Typography
} from '@mui/material';
import { userApi } from '../../../api/user-api';
import { useMounted } from '../../../hooks/use-mounted';
import { Scrollbar } from '../../scrollbar';
import { SeverityPill } from '../../severity-pill';
import { ArrowDropDown, ArrowDropUp } from '@mui/icons-material';

export const CustomerLogs = (props) => {
  const { admin_id, customerid, ...other } = props;
  const isMounted = useMounted();
  const [logs, setLogs] = useState([]);
  const [types, setTypes] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(25);
  const [sortDirection, setSortDirection] = useState('desc');
  const [selectedType, setSelectedType] = useState('');
  const [loading, setLoading] = useState(true);

  // Pagination, filtre et tri sont traités côté serveur : la page affichée est
  // la seule chargée, et le tri porte sur l'ensemble des activités.
  const getLogs = useCallback(async (pageNumber, pageSize, type, sort) => {
    setLoading(true);
    try {
      const data = await userApi.getUsersActivities(admin_id, customerid, pageNumber, pageSize, type, sort);
      if (isMounted()) {
        setLogs(data.results || []);
        setTotal(data.count || 0);
        if (data.types) {
          setTypes(data.types);
        }
      }
    } catch (err) {
      console.error(err);
      if (isMounted()) {
        setLogs([]);
        setTotal(0);
      }
    } finally {
      if (isMounted()) {
        setLoading(false);
      }
    }
  }, [admin_id, customerid, isMounted]);

  // Chargement initial uniquement : les changements de page, de filtre et de
  // tri déclenchent eux-mêmes leur propre appel via les handlers.
  useEffect(() => {
    getLogs(1, limit, selectedType, sortDirection);
  }, []);

  const handlePageChange = (_event, newPage) => {
    setPage(newPage);
    getLogs(newPage + 1, limit, selectedType, sortDirection);
  };

  const handleLimitChange = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    setLimit(newLimit);
    setPage(0);
    getLogs(1, newLimit, selectedType, sortDirection);
  };

  const handleTypeChange = (event) => {
    const newType = event.target.value;
    setSelectedType(newType);
    setPage(0);
    getLogs(1, limit, newType, sortDirection);
  };

  const handleSortClick = () => {
    const newSort = sortDirection === 'desc' ? 'asc' : 'desc';
    setSortDirection(newSort);
    setPage(0);
    getLogs(1, limit, selectedType, newSort);
  };

  return (
    <Card {...other}>
      <CardHeader title="Activités" />
      <Divider />
      <Scrollbar>
        <Select
          value={selectedType}
          displayEmpty
          onChange={handleTypeChange}
          sx={{
            minWidth: '10em',
            maxHeight: '3.5em',
            borderRadius: '4px',
            padding: '8px',
            margin: '2%',
            border: 'none',
            backgroundColor: (theme) => theme.palette.mode === "dark" ? "#555B61" : "#FFFFFF"
          }}
          IconComponent={ArrowDropDown}
        >
          <MenuItem value="">Tous les types</MenuItem>
          {types.map((type) => (
            <MenuItem key={type} value={type}>{type}</MenuItem>
          ))}
        </Select>

        <TablePagination
          component="div"
          count={total}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleLimitChange}
          page={page}
          labelRowsPerPage='Lignes par page :'
          rowsPerPage={limit}
          rowsPerPageOptions={[25, 50, 100]}
          labelDisplayedRows={({ from, to, count }) =>
            `${from}–${to} sur ${count !== -1 ? count : `plus de ${to}`}`
          }
        />

        <Table>
          <TableHead>
            <TableRow>
              <TableCell align="center">Type</TableCell>
              <TableCell align="center">Description</TableCell>
              <TableCell align="center">
                <Typography
                  onClick={handleSortClick}
                  style={{ cursor: 'pointer' }}
                >
                  Date de création
                  {sortDirection === 'desc'
                    ? <ArrowDropDown fontSize="small" />
                    : <ArrowDropUp fontSize="small" />}
                </Typography>
              </TableCell>
              <TableCell align="center">ID du cours</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Box sx={{ py: 3 }}><CircularProgress size={28} /></Box>
                </TableCell>
              </TableRow>
            )}
            {!loading && logs.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Box sx={{ py: 3 }}>Aucune activité</Box>
                </TableCell>
              </TableRow>
            )}
            {!loading && logs.map((log) => (
              <TableRow key={log.id}>
                <TableCell align="center">
                  <SeverityPill color={log.type !== 'acces' ? 'success' : 'warning'}>
                    {log.type}
                  </SeverityPill>
                </TableCell>
                {/* description stockée avec du HTML (liens) — même rendu que la page Activités */}
                <TableCell align="center">
                  <div dangerouslySetInnerHTML={{ __html: log.description }} />
                </TableCell>
                <TableCell align="center">{log.created_at}</TableCell>
                <TableCell align="center">{log.courses}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Scrollbar>
    </Card>
  );
};
